import useSWR from "swr";

import { getServerUrl } from "config/backend";
import { USD_DECIMALS, numberToBigint } from "lib/numbers";
import { appendOracleNetworkParam } from "lib/oracleNetwork";

const LATEST_CANDLE_REFRESH_INTERVAL_MS: Record<string, number> = {
  "1": 15_000,
  "1m": 15_000,
  "5": 30_000,
  "5m": 30_000,
  "15": 45_000,
  "15m": 45_000,
  "60": 60_000,
  "1h": 60_000,
  "240": 120_000,
  "4h": 120_000,
  "1d": 180_000,
  "1w": 300_000,
  "1M": 600_000,
};

function getRefreshInterval(period: string): number {
  return LATEST_CANDLE_REFRESH_INTERVAL_MS[period] ?? 30_000;
}

export function useLatestCandleClose(chainId: number, symbol: string | undefined, period: string) {
  const key = symbol ? ["chart-last-candle", chainId, symbol, period] : null;
  const { data } = useSWR(
    key,
    async () => {
      if (!symbol) return undefined;
      const baseUrl = getServerUrl(chainId, "/oracle-keeper/prices/candles");
      const params = new URLSearchParams({
        tokenSymbol: symbol,
        period,
        limit: "1",
      });
      appendOracleNetworkParam(params, chainId);

      const res = await fetch(`${baseUrl}?${params.toString()}`);
      if (!res.ok) return undefined;
      const json = await res.json();
      const candles = Array.isArray(json?.candles) ? json.candles : [];
      if (!candles.length) return undefined;
      const last = candles[candles.length - 1];
      const close = Number(last?.[4]);
      if (!Number.isFinite(close) || close <= 0) return undefined;
      return numberToBigint(close, USD_DECIMALS);
    },
    {
      refreshInterval: getRefreshInterval(period),
      errorRetryInterval: 5000,
      keepPreviousData: true,
      revalidateOnFocus: false,
    }
  );

  return data as bigint | undefined;
}
